/* ==========================================================================
   Kelva — JavaScript global. Aucune dépendance externe.
   ========================================================================== */

const Kelva = (window.Kelva = window.Kelva || {});

Kelva.settings = window.themeSettings || {};

/* --------------------------------------------------------- Utilitaires --- */

Kelva.money = function (cents) {
  const format = Kelva.settings.moneyFormat || '{{amount}}';
  const value = (cents / 100).toFixed(2);
  const parts = value.split('.');
  const withSeparators = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return format
    .replace(/\{\{\s*amount\s*\}\}/, withSeparators + ',' + parts[1])
    .replace(/\{\{\s*amount_no_decimals\s*\}\}/, withSeparators)
    .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, withSeparators + ',' + parts[1])
    .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, withSeparators);
};

Kelva.debounce = function (fn, wait) {
  let t;
  return function () {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, arguments), wait);
  };
};

Kelva.toast = function (message) {
  let el = document.querySelector('.toast');
  if (!el) {
    el = document.createElement('div');
    el.className = 'toast';
    el.setAttribute('role', 'status');
    document.body.appendChild(el);
  }
  el.textContent = message;
  requestAnimationFrame(() => el.classList.add('is-visible'));
  clearTimeout(el._timer);
  el._timer = setTimeout(() => el.classList.remove('is-visible'), 2600);
};

/* ------------------------------------------------------------- Drawers --- */

Kelva.drawers = {
  activeId: null,
  lastFocus: null,

  open(id) {
    const drawer = document.getElementById(id);
    if (!drawer) return;
    this.lastFocus = document.activeElement;
    this.activeId = id;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    const overlay = document.querySelector('[data-overlay]');
    if (overlay) overlay.classList.add('is-active');
    document.body.classList.add('is-locked');
    const focusable = drawer.querySelector('[data-drawer-close], button, a, input');
    if (focusable) setTimeout(() => focusable.focus(), 120);
    document.dispatchEvent(new CustomEvent('kelva:drawer:open', { detail: { id } }));
  },

  close() {
    document.querySelectorAll('.drawer.is-open').forEach((d) => {
      d.classList.remove('is-open');
      d.setAttribute('aria-hidden', 'true');
    });
    const overlay = document.querySelector('[data-overlay]');
    if (overlay) overlay.classList.remove('is-active');
    document.body.classList.remove('is-locked');
    if (this.lastFocus) this.lastFocus.focus();
    this.activeId = null;
  },
};

function initDrawerTriggers() {
  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-drawer-open]');
    if (opener) {
      event.preventDefault();
      Kelva.drawers.open(opener.getAttribute('data-drawer-open'));
      return;
    }
    if (event.target.closest('[data-drawer-close]') || event.target.closest('[data-overlay]')) {
      Kelva.drawers.close();
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && Kelva.drawers.activeId) Kelva.drawers.close();
    if (event.key !== 'Tab' || !Kelva.drawers.activeId) return;
    const drawer = document.getElementById(Kelva.drawers.activeId);
    if (!drawer) return;
    const items = drawer.querySelectorAll(
      'a[href], button:not([disabled]), input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });
}

/* --------------------------------------------------------- Accordéons ---- */

function initAccordions(scope) {
  (scope || document).querySelectorAll('[data-accordion-trigger]').forEach((trigger) => {
    if (trigger.dataset.bound) return;
    trigger.dataset.bound = 'true';
    trigger.addEventListener('click', () => {
      const item = trigger.closest('.accordion__item');
      const group = trigger.closest('[data-accordion-single]');
      const open = item.classList.contains('is-open');
      if (group) {
        group.querySelectorAll('.accordion__item').forEach((other) => {
          other.classList.remove('is-open');
          const t = other.querySelector('[data-accordion-trigger]');
          if (t) t.setAttribute('aria-expanded', 'false');
        });
      }
      item.classList.toggle('is-open', !open);
      trigger.setAttribute('aria-expanded', String(!open));
    });
  });
}

/* -------------------------------------------- Révélations au scroll ------ */

function initAnimations(scope) {
  const root = scope || document;
  if (document.documentElement.classList.contains('no-animations')) return;
  if (!('IntersectionObserver' in window)) {
    root.querySelectorAll('[data-animate]').forEach((el) => el.classList.add('is-visible'));
    return;
  }
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
  );
  root.querySelectorAll('[data-animate]:not(.is-visible)').forEach((el, index) => {
    if (!el.style.getPropertyValue('--animation-delay')) {
      const stagger = el.closest('[data-stagger]');
      if (stagger) el.style.setProperty('--animation-delay', index * 90 + 'ms');
    }
    observer.observe(el);
  });
}

/* --------------------------------------------------------- Parallaxe ----- */

function initParallax() {
  const targets = document.querySelectorAll('[data-parallax]');
  if (!targets.length || !Kelva.settings.parallax) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  let ticking = false;
  const update = () => {
    targets.forEach((el) => {
      const rect = el.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > window.innerHeight) return;
      const progress = (rect.top + rect.height / 2 - window.innerHeight / 2) / window.innerHeight;
      const amount = parseFloat(el.dataset.parallax) || 20;
      el.style.transform = `translate3d(0, ${(-progress * amount).toFixed(2)}px, 0)`;
    });
    ticking = false;
  };
  window.addEventListener(
    'scroll',
    () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    },
    { passive: true }
  );
  update();
}

/* ------------------------------------------------------------- Header ---- */

function initHeader() {
  const wrap = document.querySelector('[data-header]');
  if (!wrap) return;
  let lastY = window.scrollY;
  const threshold = 60;

  const onScroll = () => {
    const y = window.scrollY;
    wrap.classList.toggle('is-scrolled', y > threshold);
    if (wrap.dataset.hideOnScroll === 'true') {
      wrap.classList.toggle('is-hidden', y > lastY && y > 300 && !Kelva.drawers.activeId);
    }
    lastY = y;
  };
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();
}

/* --------------------------------------------- Menu mobile (sous-menus) -- */

function initMobileNav() {
  document.querySelectorAll('[data-mobile-toggle]').forEach((btn) => {
    if (btn.dataset.bound) return;
    btn.dataset.bound = 'true';
    btn.addEventListener('click', () => {
      const parent = btn.closest('li');
      const sub = parent.querySelector('.mobile-nav__sub');
      if (!sub) return;
      const open = parent.classList.toggle('is-open');
      sub.hidden = !open;
      btn.setAttribute('aria-expanded', String(open));
    });
  });
}

/* -------------------------------------------------------- Carrousels ----- */

function initCarousels(scope) {
  (scope || document).querySelectorAll('[data-carousel]').forEach((carousel) => {
    if (carousel.dataset.bound) return;
    carousel.dataset.bound = 'true';
    const track = carousel.querySelector('[data-carousel-track]');
    if (!track) return;
    carousel.querySelectorAll('[data-carousel-prev]').forEach((btn) =>
      btn.addEventListener('click', () => {
        track.scrollBy({ left: -track.clientWidth * 0.8, behavior: 'smooth' });
      })
    );
    carousel.querySelectorAll('[data-carousel-next]').forEach((btn) =>
      btn.addEventListener('click', () => {
        track.scrollBy({ left: track.clientWidth * 0.8, behavior: 'smooth' });
      })
    );
  });
}

/* ---------------------------------------------------- Barre d'annonce ---- */

function initAnnouncement(scope) {
  (scope || document).querySelectorAll('[data-announcement]').forEach((bar) => {
    if (bar.dataset.bound) return;
    bar.dataset.bound = 'true';
    const slides = bar.querySelectorAll('.announcement__slide');
    if (slides.length < 2) return;
    let index = 0;
    const go = (next) => {
      slides[index].classList.remove('is-active');
      index = (next + slides.length) % slides.length;
      slides[index].classList.add('is-active');
    };
    const interval = parseInt(bar.dataset.announcement, 10) || 5000;
    let timer = setInterval(() => go(index + 1), interval);
    bar.querySelectorAll('[data-announcement-prev]').forEach((b) =>
      b.addEventListener('click', () => {
        clearInterval(timer);
        go(index - 1);
        timer = setInterval(() => go(index + 1), interval);
      })
    );
    bar.querySelectorAll('[data-announcement-next]').forEach((b) =>
      b.addEventListener('click', () => {
        clearInterval(timer);
        go(index + 1);
        timer = setInterval(() => go(index + 1), interval);
      })
    );
  });
}

/* --------------------------------------------------------- Quantités ----- */

function initQuantities(scope) {
  (scope || document).querySelectorAll('[data-quantity]').forEach((widget) => {
    if (widget.dataset.bound) return;
    widget.dataset.bound = 'true';
    const input = widget.querySelector('input');
    widget.querySelectorAll('[data-qty-change]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const step = parseInt(btn.dataset.qtyChange, 10);
        const min = parseInt(input.min, 10) || 1;
        const value = Math.max(min, (parseInt(input.value, 10) || min) + step);
        input.value = value;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  });
}

/* ------------------------------------------------ Recherche prédictive --- */

function initPredictiveSearch() {
  const form = document.querySelector('[data-predictive-form]');
  if (!form) return;
  const input = form.querySelector('input[type="search"]');
  const results = document.querySelector('[data-predictive-results]');
  if (!input || !results) return;

  const search = Kelva.debounce(async () => {
    const term = input.value.trim();
    if (term.length < 2) {
      results.innerHTML = '';
      return;
    }
    try {
      const response = await fetch(
        `${Kelva.settings.routes.predictive_search_url}?q=${encodeURIComponent(term)}` +
          `&resources[type]=product,collection,page&resources[limit]=6&section_id=predictive-search`
      );
      if (!response.ok) throw new Error('search');
      const text = await response.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const content = doc.querySelector('[data-predictive-results]');
      results.innerHTML = content ? content.innerHTML : '';
    } catch (error) {
      results.innerHTML = '';
    }
  }, 280);

  input.addEventListener('input', search);
}

/* ------------------------------------------------- Barre d'achat mobile -- */

function initStickyAtc() {
  const bar = document.querySelector('[data-sticky-atc]');
  const anchor = document.querySelector('[data-atc-anchor]');
  if (!bar || !anchor || !('IntersectionObserver' in window)) return;
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => bar.classList.toggle('is-visible', !entry.isIntersecting));
    },
    { rootMargin: '-120px 0px 0px 0px' }
  );
  observer.observe(anchor);
}

/* ------------------------------------------------------ Initialisation --- */

function initAll(scope) {
  initAccordions(scope);
  initAnimations(scope);
  initCarousels(scope);
  initAnnouncement(scope);
  initQuantities(scope);
  initMobileNav();
}

document.addEventListener('DOMContentLoaded', () => {
  initDrawerTriggers();
  initHeader();
  initParallax();
  initPredictiveSearch();
  initStickyAtc();
  initAll(document);
});

/* Rechargement des sections dans l'éditeur de thème */
document.addEventListener('shopify:section:load', (event) => {
  initAll(event.target);
  initHeader();
  initStickyAtc();
});
document.addEventListener('shopify:section:select', (event) => {
  event.target.querySelectorAll('[data-animate]').forEach((el) => el.classList.add('is-visible'));
});

Kelva.initAll = initAll;
Kelva.initAnimations = initAnimations;
